import { client } from '@groupe-trousseau/shared/api-client/client';

/**
 * ═══════════════════════════════════════════════════════════════════════════
 * **L'ENROBAGE DU CLIENT D'API — LE SEUL ENDROIT, ET C'EST TOUT SON OBJET.**
 * ═══════════════════════════════════════════════════════════════════════════
 *
 * `DOCTRINE.md` §4 : « **aucun appel réseau nu.** Aucun `fetch` ni `axios` en dehors du
 * client généré et de sa couche d'enrobage. C'est ce qui garantit **un seul endroit pour
 * poser le jeton d'authentification, un seul endroit pour traiter un 401, un seul endroit
 * pour consommer un 426**. » Et `admin/CLAUDE.md` le répète en interdit : « aucun `fetch`
 * ni `axios` : le client généré uniquement ».
 *
 * ───────────────────────────────────────────────────────────────────────────
 * **POURQUOI CE FICHIER N'EST PAS CELUI DE `web/`, ALORS QU'IL LUI RESSEMBLE.**
 * ───────────────────────────────────────────────────────────────────────────
 *
 * `web/src/shared/api/client.ts` fait la même chose, et **on ne le partage pas**.
 * `DOCTRINE.md` §3 : « `web/`, `admin/` et `mobile/` **ne se parlent jamais entre eux**.
 * Aucun import direct, aucun composant partagé. **Cette règle est la plus importante du
 * fichier.** » Et §7 le redit sur ce cas précis : « la tentation de partager sera forte.
 * Elle est refusée. »
 *
 * **Ce qui est partagé est le client généré**, dans `shared/api-client/`, et il l'est
 * parce qu'il est **produit** par le contrat et non écrit : c'est exactement la frontière
 * que §4 trace. L'enrobage, lui, porte des décisions de surface — où renvoyer une session
 * tombée, quelle version annoncer — et ces décisions diffèrent d'une surface à l'autre.
 *
 * ───────────────────────────────────────────────────────────────────────────
 * **LE JETON DE SESSION N'APPARAÎT PAS ICI, ET C'EST VOULU.**
 * ───────────────────────────────────────────────────────────────────────────
 *
 * D-065 : la session voyage dans un cookie `httpOnly`, donc **aucun script de cette page
 * ne peut la lire** — ni celui-ci, ni un script injecté. Deux choses la remplacent, et
 * elles sont les mêmes que dans `web/` parce que c'est le serveur qui les impose :
 *
 * 1. **`credentials: 'include'`**, sans quoi le navigateur n'envoie pas le cookie vers une
 *    autre origine — et `admin.swype.fr` appelant `api.swype.fr` en est une. Le symptôme
 *    serait un `401` sur une personne connectée, qui ne désigne pas sa cause.
 * 2. **Le jeton anti-CSRF, lu dans SON cookie et recopié dans un en-tête.** Le navigateur
 *    renvoie le cookie tout seul, mais **seul un script de la bonne origine peut le lire**
 *    pour le recopier. Un site tiers déclenche bien la requête avec le cookie de session,
 *    et il n'a pas l'en-tête.
 *
 * **Cette seconde ligne compte plus ici que partout ailleurs** : `CDC-5` est une surface
 * « **presque entièrement de l'écriture** », et sans `CSRF_SECRET` côté serveur la lecture
 * continue tandis que **toute écriture depuis un navigateur connecté échoue**. C'est le
 * geste de déploiement que la remise du lot rappelle.
 */

/**
 * **Le nom du cookie anti-CSRF, et il n'est pas `httpOnly`** — c'est précisément ce qui
 * permet de le lire ici. Le préfixe `__Secure-` tombe en développement, où `secure` est
 * impossible sur `http://` : les deux noms sont essayés, et l'ordre n'importe pas
 * puisqu'un seul existe.
 */
const NOMS_DU_COOKIE_CSRF = ['__Secure-swype-csrf', 'swype-csrf'] as const;

/** L'en-tête où le jeton anti-CSRF se recopie. */
const EN_TETE_CSRF = 'x-csrf-token';

/** L'en-tête de version du client, mécanisme `426` de `DOCTRINE.md` §5. */
const EN_TETE_VERSION = 'x-app-version';

function lireLeJetonCsrf(): string | null {
  for (const nom of NOMS_DU_COOKIE_CSRF) {
    for (const morceau of document.cookie.split(';')) {
      const separateur = morceau.indexOf('=');
      if (separateur === -1) continue;
      if (morceau.slice(0, separateur).trim() !== nom) continue;
      const valeur = decodeURIComponent(morceau.slice(separateur + 1).trim());
      if (valeur !== '') return valeur;
    }
  }
  return null;
}

/**
 * **L'adresse de l'API vient de l'environnement de build, jamais d'un littéral.**
 *
 * Ce n'est pas un secret — c'est une adresse publique que le navigateur montre à chaque
 * requête —, mais elle change par environnement. **Le repli local ne vaut qu'en
 * développement** : sans lui, `npm run dev` sans fichier d'environnement produit des
 * requêtes vers la page elle-même, et l'erreur désigne une route absente au lieu d'une
 * variable manquante.
 */
const ADRESSE_API: string = import.meta.env.VITE_API_URL ?? 'http://127.0.0.1:3000';

/**
 * **L'adresse de la page de connexion, chez `site/`.**
 *
 * `CDC-5` F-55 : cette surface « s'atteint par la page de connexion **unique** du site
 * vitrine, celle des quatre rôles. **Aucune adresse cachée, aucun sélecteur de rôle** ».
 * `admin/` ne porte donc **aucun formulaire de connexion**, et la peau en livre un que
 * `Administration.tsx` remplace pour cette raison.
 */
export function adresseDeConnexion(): string | null {
  const site = import.meta.env.VITE_SITE_URL;
  // **RIEN, ET SURTOUT PAS `'/'`. UN REPLI À LA RACINE EST UNE BOUCLE INFINIE.**
  //
  // La première version rendait `'/'` quand la variable manquait, par symétrie avec le repli
  // de `VITE_API_URL`. Les deux replis ne se valent pas : celui de l'API produit un appel qui
  // échoue, celui-ci produit **une redirection vers la page qui redirige ici**.
  //
  // Constaté en recette le 20260824, et c'est exactement la panne : sans les deux variables,
  // `checkAuth` échoue faute d'API, `ra-core` renvoie sur `/login`, la page de renvoi renvoie
  // sur `/`, `checkAuth` échoue encore — **et le navigateur recharge sans fin**. Aucune erreur
  // n'apparaît, aucun message ne nomme la variable manquante, et l'écran est inutilisable.
  //
  // `null` oblige l'appelant à traiter le cas, et le compilateur l'y force. C'est ce que
  // `VITE_API_URL` fait déjà de son côté en nommant son repli « explicite et valant seulement
  // en développement » : ici il n'y a aucun repli qui vaille, donc il n'y en a pas.
  if (site === undefined || site === '') return null;
  return `${site.replace(/\/$/, '')}/connexion`;
}

/**
 * **L'adresse de l'application de séance, `web/` — et elle ne sert qu'à UN renvoi.**
 *
 * `CDC-2` F-20 : la surface d'observation d'un cours en direct **vit dans `web/`**, à une
 * adresse en lecture stricte, et cette surface-ci **y renvoie par URL**. Le motif est un port
 * d'architecture : le moteur de tableau vit dans `web/src/features/cours/moteur.tsx`, **seul
 * fichier du dépôt autorisé à le connaître** — port étroit de D-012, gardé par quatre sondes.
 * Le faire importer par `admin/` casserait ce port, le dupliquer créerait une seconde vérité.
 * **Un renvoi par URL n'est pas un import**, et c'est pourquoi il passe par une variable et
 * non par un chemin de module.
 *
 * **AUCUN REPLI, DÉLIBÉRÉMENT, ET C'EST LE MÊME MOTIF QUE `adresseDeConnexion()`** — « un
 * repli `'/'` produisait une boucle infinie, constatée en recette le 20260824 ». Ici la panne
 * serait plus discrète et pas meilleure : un renvoi relatif ouvrirait un onglet sur
 * `admin.swype.fr/observation/…`, une adresse que cette surface ne sert pas, donc l'index de
 * l'application d'administration — **qui a l'air de marcher**. `null` oblige l'appelant à
 * traiter le cas, et le compilateur l'y force : l'écran nomme alors la variable manquante au
 * lieu de renvoyer nulle part.
 */
export function adresseDeLApplication(): string | null {
  const application = import.meta.env.VITE_APP_URL;
  if (application === undefined || application === '') return null;
  return application.replace(/\/$/, '');
}

/** Le domaine du contenu utilisateur, D-005. Il ne sert qu'au logo d'une structure. */
export function adresseDuContenu(): string {
  const contenu = import.meta.env.VITE_CONTENU_URL;
  return contenu === undefined ? '' : contenu.replace(/\/$/, '');
}

export function configurerLeClient(): void {
  client.setConfig({
    baseUrl: ADRESSE_API,
    credentials: 'include',
  });

  client.interceptors.request.use((requete) => {
    const csrf = lireLeJetonCsrf();
    if (csrf !== null) requete.headers.set(EN_TETE_CSRF, csrf);
    requete.headers.set(EN_TETE_VERSION, __VERSION_CLIENTE__);
    return requete;
  });
}

/**
 * **Ce qu'une erreur d'API dit à un écran, et rien de plus.**
 *
 * `DOCTRINE.md` §5 : « **`code` est le contrat réel** : c'est sur lui que le front branche
 * son comportement, **jamais sur `message`**, qui est destiné à l'humain et peut changer
 * sans préavis. »
 *
 * **Cette surface en dépend plus que les autres**, et `CDC-5` en nomme sept : `SECRET_NON_LISIBLE`,
 * `VALEUR_HORS_BORNES`, `REGLAGE_DE_PARC_INTERDIT`, `CHIFFRES_DE_PARC_INTERDITS`,
 * `AUCUN_ELEVE_DE_CETTE_STRUCTURE`, `SURFACE_ADMINISTRATION_INTERDITE`,
 * `EXPORT_SEPARATEUR_AMBIGU`. D-074 a posé `@CodeDeRefus` côté serveur pour que deux
 * d'entre eux **portent leur nom** au lieu du générique `FUNCTION_NOT_ALLOWED` — « un
 * front qui attend le code nommé ne l'aurait jamais vu ». C'est ici qu'il est lu.
 */
export type ErreurDApi = {
  readonly code: string;
  readonly message: string;
  readonly statut: number | null;
};

const MESSAGE_DE_REPLI =
  'Le produit n’a pas pu joindre le serveur. Réessayez dans un instant.';

export function lireLErreur(erreur: unknown): ErreurDApi {
  const enveloppe = erreur as
    | { code?: unknown; message?: unknown; statusCode?: unknown }
    | null
    | undefined;

  return {
    code: typeof enveloppe?.code === 'string' ? enveloppe.code : 'INJOIGNABLE',
    message:
      typeof enveloppe?.message === 'string' ? enveloppe.message : MESSAGE_DE_REPLI,
    statut: typeof enveloppe?.statusCode === 'number' ? enveloppe.statusCode : null,
  };
}

/**
 * **Déballe une réponse du client généré, ou lève.**
 *
 * Le client rend `{ data, error }` où **les deux sont facultatifs** : `data` est typé
 * `T | undefined` même quand `error` est absent, parce que le générateur ne sait pas
 * qu'une route qui répond `200` porte toujours un corps. **Déballer à la main dans chaque
 * appel produirait la même garde recopiée vingt-deux fois**, et la vingt-deuxième serait
 * oubliée.
 *
 * **Ce qui est levé est l'enveloppe, pas une `Error`.** `DOCTRINE.md` §5 en fixe la forme
 * — `{ code, message, statusCode }` — et `ra-core` la fait remonter telle quelle à
 * l'écran, qui branche sur `code` par `lireLErreur`.
 */
export async function deballer<T>(
  appel: Promise<{ data?: T; error?: unknown }>,
): Promise<T> {
  const reponse = await appel;
  // eslint-disable-next-line @typescript-eslint/only-throw-error
  if (reponse.error !== undefined) throw reponse.error;
  if (reponse.data === undefined) {
    throw new Error('Le serveur a répondu sans corps là où le contrat en déclare un.');
  }
  return reponse.data;
}
